import { ReactNode, createContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import * as actions from "./ducks/notifications";
import { getMatchings, getSmartMatches } from "./ducks/smart-match";
import { deleteSmartMatch } from "./ducks/smart-match";
import * as driverActivities from "./ducks/data/driver-activities";
import * as cargoActions from "./ducks/data/cargos";
import * as routingActions from "./ducks/data/routing";
import * as productsActions from "./ducks/data/products";
import * as routeActions from "./ducks/routes";
import * as clientActions from "./ducks/data/clients";
import * as featureActions from "./ducks/features";
import * as customerDeliveryLocationActions from "./ducks/data/economics/customer-delivery-locations";
import { failedInvoiceUpdated } from "./ducks/data/economics/invoices";
import { invoiceT } from "./ducks/data/economics/invoices/types";
import * as upcomingPageActions from "./ducks/app/upcoming-page";
import * as driversActions from "./ducks/data/drivers";
import * as driverTimeReservedActions from "./ducks/data/driver-time-reserved";
import { useAppDispatch } from "./redux-store";
import { reloadRoute } from "./ducks/app/route-info/routing";
import {
  routeT,
  customerDeliveryLocationT,
  driverTimeReservedUpdatedEventT,
} from "dora-contracts";
import * as t from "io-ts";
import { selectUser } from "./ducks/auth/selectors";
import * as authActions from "./ducks/auth";
import { useSelector } from "react-redux";
import { routeInfoStopsT } from "dora-contracts";
import { createProductsAndSurchargesT } from "./ducks/data/products/types";
import { reportWarning } from "./helpers/error-helper";
import { createSocketUpdateBuffer } from "./socket-update-buffer";
import { createReconnectResync } from "./reconnect-resync";
import { resyncAfterReconnect } from "./resync-after-reconnect";
import { customerContactT } from "./ducks/data/economics/customer-contacts/types";

export const SocketContext = createContext<Socket | null>(null);

const SocketConnection = ({ children }: { children?: ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const dispatch = useAppDispatch();
  const me = useSelector(selectUser);
  useEffect(() => {
    // The server refuses a socket without a logged-in session, and this
    // effect runs again once the user logs in.
    if (!me) {
      setSocket(null);
      return;
    }
    console.log("Establishing connection to server");
    const connection = io("/", {
      transports: ["websocket"],
      path: "/socket",
    });

    // Batch client events to prevent flooding Redux with thousands of dispatches
    const clientBuffer = new Map<string, any>();
    let clientFlushTimer: NodeJS.Timeout | null = null;
    const CHUNK_SIZE = 200; // Process 200 clients at a time
    const CHUNK_DELAY = 50; // 50ms between chunks to keep UI responsive
    const INITIAL_BATCH_DELAY = 500; // Wait 500ms to accumulate events before processing

    const flushClientBuffers = () => {
      if (clientBuffer.size > 0) {
        const allClients = Array.from(clientBuffer.values());
        clientBuffer.clear();

        // Process in chunks to avoid blocking the main thread
        let chunkIndex = 0;
        const processNextChunk = () => {
          const chunk = allClients.slice(chunkIndex, chunkIndex + CHUNK_SIZE);
          if (chunk.length > 0) {
            dispatch(clientActions.clientsUpdated(chunk));
            chunkIndex += CHUNK_SIZE;
            if (chunkIndex < allClients.length) {
              setTimeout(processNextChunk, CHUNK_DELAY);
            }
          }
        };
        processNextChunk();
      }
      clientFlushTimer = null;
    };

    const scheduleClientFlush = () => {
      if (!clientFlushTimer) {
        clientFlushTimer = setTimeout(flushClientBuffers, INITIAL_BATCH_DELAY);
      }
    };

    const updateBuffer = createSocketUpdateBuffer(dispatch);

    const reconnectResync = createReconnectResync(() => {
      dispatch(resyncAfterReconnect());
    });

    let hasConnectedBefore = false;
    connection.on("connect", () => {
      if (!hasConnectedBefore) {
        hasConnectedBefore = true;
        return;
      }
      reportWarning({
        event: "socket-reconnected",
        userId: me?.id,
      });
      if (me) {
        reconnectResync.schedule();
      }
    });

    // The server drops a socket whose handshake carried no logged-in session
    // (e.g. this tab reconnected while logged out, and the user has since
    // logged back in from another tab). socket.io doesn't auto-reconnect after
    // a server-side disconnect, so re-handshake to pick up the current
    // session cookie. Throttled so a genuinely dead session can't loop.
    let lastForcedReconnectAt = 0;
    connection.on("disconnect", (reason) => {
      if (reason !== "io server disconnect") {
        return;
      }
      if (Date.now() - lastForcedReconnectAt < 30_000) {
        return;
      }
      lastForcedReconnectAt = Date.now();
      connection.connect();
    });

    // A reconnect while the session is logged out is refused at the handshake
    // with "unauthorized" (SOCKET_UNAUTHORIZED on the server), and socket.io
    // doesn't retry a refused handshake. The session may have expired, or the
    // user may have logged out and back in from another tab, and `me` doesn't
    // change in this tab either way, so ask the server once which it is. An
    // expired session logs this tab out, which closes the socket for good;
    // only an inconclusive check is retried. Checks are at least 30s apart, so
    // a session /auth/me accepts but the handshake still refuses can't loop.
    const RECHECK_INTERVAL_MS = 30_000;
    let unauthorizedRetryTimer: NodeJS.Timeout | null = null;
    let recheckInProgress = false;
    let lastRecheckAt = 0;
    let closed = false;
    const scheduleRecheck = (delayMs: number) => {
      unauthorizedRetryTimer = setTimeout(() => {
        unauthorizedRetryTimer = null;
        void recheckSessionAndReconnect();
      }, delayMs);
    };
    const recheckSessionAndReconnect = async () => {
      recheckInProgress = true;
      lastRecheckAt = Date.now();
      const result = await dispatch(authActions.recheckSession());
      recheckInProgress = false;
      if (closed || !authActions.recheckSession.fulfilled.match(result)) {
        return;
      }
      if (result.payload === "RECONNECT") {
        connection.connect();
      } else if (result.payload === "RETRY_LATER") {
        scheduleRecheck(RECHECK_INTERVAL_MS);
      }
    };
    connection.on("connect_error", (err) => {
      if (
        err.message !== "unauthorized" ||
        unauthorizedRetryTimer ||
        recheckInProgress
      ) {
        return;
      }
      scheduleRecheck(
        Math.max(0, lastRecheckAt + RECHECK_INTERVAL_MS - Date.now())
      );
    });

    connection.onAny((evtName, evt) => {
      switch (evtName) {
        case "finance.products.synced":
          dispatch(
            productsActions.productsSynced(
              decode(createProductsAndSurchargesT, evt)
            )
          );
          break;
        case "routing.created":
        case "routing.updated": {
          dispatch(routingActions.routingUpdated(evt.routeId));
          break;
        }
        case "features.updated": {
          dispatch(featureActions.initialize());
          break;
        }
        case "cargo.updated": {
          dispatch(cargoActions.reloadCargo(evt.cargoId));
          dispatch(upcomingPageActions.refresh());
          break;
        }
        case "cargo.assigned-to-route": {
          dispatch(routeActions.routeUpdated(decode(routeT, evt)));
          dispatch(upcomingPageActions.refresh());
          break;
        }
        case "cargo-view.deleted":
        case "cargo-view.updated": {
          updateBuffer.push({
            kind: "cargo-view",
            value: evt,
          });
          break;
        }
        case "smartMatch.priceRequested":
          dispatch(
            actions.notifyL({
              namespace: "notifications",
              key: "smartMatchPriceRequested",
            })
          );
          // dispatch(smartMatchActions.addMatchings([evt]));
          //
          dispatch(getMatchings());
          break;
        case "smartMatch.priceGiven":
          dispatch(
            actions.notifyL({
              namespace: "notifications",
              key: "smartMatchPriceGiven",
            })
          );
          // TODO: Make this better, now we react to a socket message
          // by querying the backend :( We have all the data to correctly update the state.
          dispatch(getSmartMatches(evt.routeId));
          break;
        case "smartMatch.priceAccepted":
          dispatch(
            actions.notifyL({
              namespace: "notifications",
              key: "smartMatchPriceAccepted",
            })
          );
          dispatch(getMatchings());
          break;
        case "smartMatch.created":
          dispatch(getSmartMatches(evt.routeId));
          break;
        case "smartMatch.deleted":
          dispatch(
            deleteSmartMatch({
              matchId: evt.matchId,
              cargoInfoId: evt.routeId,
            })
          );
          break;
        case "activity.driver-logged-in": {
          dispatch(driverActivities.driverLoggedInActivity(evt));
          break;
        }
        case "activity.driver-arrival":
          dispatch(driverActivities.driverStopArrivedActivity(evt));
          break;
        case "activity.driver-departure":
          dispatch(driverActivities.driverStopDepartedActivity(evt));
          break;
        case "activity.driver-stop-completed":
          dispatch(driverActivities.driverStopCompletedActivity(evt));
          break;
        case "client.created":
          clientBuffer.set((evt as any).id, evt);
          scheduleClientFlush();
          break;
        case "client.updated":
          clientBuffer.set((evt as any).id, evt);
          scheduleClientFlush();
          break;
        case "client.deleted": {
          const clientId: string = (evt as any).id;
          // A buffered update for this client would otherwise flush after the
          // removal and bring it back.
          clientBuffer.delete(clientId);
          updateBuffer.push({ kind: "client-removed", value: clientId });
          break;
        }
        case "clients.removed":
          dispatch(clientActions.clientsRemoved(evt));
          break;
        case "economic.customer-contact.created":
        case "economic.customer-contact.updated":
          updateBuffer.push({
            kind: "customer-contact",
            value: {
              contact: decode(customerContactT, evt),
              created: evtName === "economic.customer-contact.created",
            },
          });
          break;
        case "customer-delivery-location.coordinates-resolved":
        case "customer-delivery-location.updated":
          dispatch(
            customerDeliveryLocationActions.deliveryLocationUpserted(
              decode(customerDeliveryLocationT, evt)
            )
          );
          break;
        case "customer-delivery-location.deleted":
          dispatch(
            customerDeliveryLocationActions.deliveryLocationDeleted(
              decode(t.strict({ id: t.string, clientId: t.string }), evt)
            )
          );
          break;
        case "route.stop-order-updated":
          const payload = decode(
            t.strict({
              routeId: t.string,
              stops: routeInfoStopsT,
            }),
            evt
          );
          dispatch(
            routeActions.stopOrderUpdated({
              ...payload,
              stops: payload.stops.map((stop) => ({
                ...stop,
                stopId: (stop as any).stopId || stop.id,
              })),
            })
          );
          dispatch(reloadRoute(payload.routeId));
          break;
        case "route-view.updated":
          updateBuffer.push({
            kind: "route-view",
            value: evt,
          });
          break;
        case "finance.failed-invoice.updated":
          dispatch(failedInvoiceUpdated(decode(invoiceT, evt)));
          break;
        case "driver.schedule-created":
          dispatch(driversActions.scheduleCreated(evt));
          break;
        case "driver.schedule-deleted":
          dispatch(driversActions.scheduleDeleted(evt));
          break;
        case "driver-time-reserved.updated":
          dispatch(
            driverTimeReservedActions.timeReservedUpdated(
              decode(driverTimeReservedUpdatedEventT, evt)
            )
          );
          break;
      }
    });
    setSocket(connection);
    // use this for debug only with __socket.disconnect() and __socket.connect()
    // from the browser console to simulate disconnets/reconnects
    // (window as any).__socket = connection;
    return () => {
      if (clientFlushTimer) {
        clearTimeout(clientFlushTimer);
        flushClientBuffers();
      }
      updateBuffer.flush();
      reconnectResync.cancel();
      closed = true;
      if (unauthorizedRetryTimer) {
        clearTimeout(unauthorizedRetryTimer);
      }
      connection.close();
    };
  }, [me, dispatch]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

const decode = <T extends t.Any>(codec: T, input: unknown): t.TypeOf<T> => {
  const result: t.TypeOf<T> = codec.decode(input);
  switch (result._tag) {
    case "Left":
      throw new Error("Error decoding data");
    case "Right":
      return result.right;
  }
};

export default SocketConnection;
